"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { AuthDialog } from "@/components/auth/AuthDialog";
import type { Dictionary, Locale } from "@/i18n/dictionaries";
import { createHeightmapStl, type HeightmapOptions } from "@/lib/heightmap-stl";
import styles from "./to3d.module.css";

type Labels = {
  title: string;
  lines: readonly string[];
  upload: string;
  change: string;
  selected: string;
  height: string;
  filter: string;
  emboss: string;
  engraved: string;
  both: string;
  smooth: string;
  create: string;
  localCreate: string;
  localReady: string;
  localDownload: string;
  localUpload: string;
  localRegenerate: string;
  localSize: string;
  localTriangles: string;
  creating: string;
  polling: string;
  localCreating: string;
  authRequired: string;
  invalidImage: string;
  failed: string;
  localFailed: string;
  ready: string;
  notConfigured: string;
};

type Props = {
  authLabels: Dictionary["authPage"];
  labels: Labels;
  locale: Locale;
};

type UploadReadiness = {
  enabled: boolean;
  status: number;
};

type LocalStlDraft = {
  blob: Blob;
  url: string;
  fileName: string;
  width: number;
  height: number;
  depth: number;
  triangleCount: number;
};

async function getUploadReadiness(): Promise<UploadReadiness> {
  const response = await fetch("/api/render/persistence/uploads/readiness", { cache: "no-store" }).catch(() => null);
  if (!response) {
    return { enabled: false, status: 0 };
  }
  const payload = (await response.json().catch(() => null)) as { enabled?: boolean } | null;
  return { enabled: Boolean(response.ok && payload?.enabled), status: response.status };
}

export function To3dUploadClient({ authLabels, labels, locale }: Readonly<Props>) {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [height, setHeight] = useState(6);
  const [mode, setMode] = useState<HeightmapOptions["mode"]>("emboss");
  const [smooth, setSmooth] = useState(true);
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [localCreating, setLocalCreating] = useState(false);
  const [localDraft, setLocalDraft] = useState<LocalStlDraft | null>(null);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  useEffect(() => () => {
    if (localDraft?.url) URL.revokeObjectURL(localDraft.url);
  }, [localDraft]);

  function clearLocalDraft() {
    setLocalDraft(null);
  }

  function chooseFile(next?: File) {
    if (!next) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(next.type) || next.size > 4.5 * 1024 * 1024) {
      setStatus(labels.invalidImage);
      return;
    }
    clearLocalDraft();
    setFile(next);
    setPreview(URL.createObjectURL(next));
    setStatus(labels.ready);
  }

  async function createAiModel() {
    if (!file || creating || localCreating) return;
    setCreating(true);
    setStatus(labels.creating);
    try {
      const readiness = await getUploadReadiness();
      if (!readiness.enabled) {
        if (readiness.status === 401) {
          setAuthOpen(true);
          setStatus(labels.authRequired);
          return;
        }
        setStatus(labels.failed);
        return;
      }

      const formData = new FormData();
      formData.append("image", file);
      const submit = await fetch("/api/hunyuan3d/jobs", { method: "POST", body: formData });
      if (submit.status === 401) {
        setAuthOpen(true);
        setStatus(labels.authRequired);
        return;
      }
      const job = await submit.json().catch(() => null) as { ok?: boolean; status?: string; jobId?: string; mode?: string; originalFileName?: string; message?: string } | null;
      if (!submit.ok || !job?.ok || !job.jobId) {
        setStatus(job?.message || (job?.status === "not-configured" ? labels.notConfigured : labels.failed));
        return;
      }

      setStatus(labels.polling);
      for (let attempt = 0; attempt < 72; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, attempt < 2 ? 2500 : 5000));
        const poll = await fetch(`/api/hunyuan3d/jobs/${encodeURIComponent(job.jobId)}?mode=${encodeURIComponent(job.mode || "pro")}&fileName=${encodeURIComponent(job.originalFileName || file.name)}`);
        if (poll.status === 401) {
          setAuthOpen(true);
          setStatus(labels.authRequired);
          return;
        }
        if (poll.status === 202) {
          setStatus(labels.polling);
          continue;
        }
        const payload = await poll.json().catch(() => null) as { record?: { id?: string }; status?: string; message?: string } | null;
        if (!poll.ok) {
          setStatus(payload?.message || (payload?.status === "not-configured" ? labels.notConfigured : labels.failed));
          return;
        }
        if (payload?.record?.id) {
          router.push(`/render-editor/${encodeURIComponent(payload.record.id)}?lang=${locale}`);
          return;
        }
        setStatus(labels.failed);
        return;
      }
      setStatus(labels.failed);
    } catch {
      setStatus(labels.failed);
    } finally {
      setCreating(false);
    }
  }

  async function createLocalModel() {
    if (!file || creating || localCreating) return;
    setLocalCreating(true);
    setStatus(labels.localCreating);
    try {
      const result = await createHeightmapStl(file, { reliefHeight: height, mode, smooth });
      const baseName = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]+/g, "-") || "heightmap";
      setLocalDraft({
        blob: result.blob,
        url: URL.createObjectURL(result.blob),
        fileName: `${baseName}.stl`,
        width: result.width,
        height: result.height,
        depth: result.depth,
        triangleCount: result.triangleCount,
      });
      setStatus(labels.localReady);
    } catch {
      setStatus(labels.localFailed);
    } finally {
      setLocalCreating(false);
    }
  }

  async function uploadLocalModel() {
    if (!localDraft || creating || localCreating) return;
    setLocalCreating(true);
    setStatus(labels.localCreating);
    try {
      const readiness = await getUploadReadiness();
      if (!readiness.enabled) {
        if (readiness.status === 401) {
          setAuthOpen(true);
          setStatus(labels.authRequired);
          return;
        }
        setStatus(labels.localFailed);
        return;
      }

      const model = new File([localDraft.blob], localDraft.fileName, { type: "model/stl" });
      const formData = new FormData();
      formData.append("file", model);
      const response = await fetch("/api/render/persistence/uploads", { method: "POST", body: formData });
      if (response.status === 401) {
        setAuthOpen(true);
        setStatus(labels.authRequired);
        return;
      }
      const payload = await response.json().catch(() => null) as { record?: { id?: string } } | null;
      if (!response.ok) {
        setStatus(labels.localFailed);
        return;
      }
      if (!payload?.record?.id) {
        setStatus(labels.localFailed);
        return;
      }
      router.push(`/render-editor/${encodeURIComponent(payload.record.id)}?lang=${locale}`);
    } catch {
      setStatus(labels.localFailed);
    } finally {
      setLocalCreating(false);
    }
  }

  return (
    <div className={styles.uploadPane}>
      <h1>{labels.title}</h1>
      {labels.lines.map((line) => <p key={line}>{line}</p>)}
      <div className={styles.uploadBox}>
        {preview ? <img src={preview} alt={file?.name ?? ""} /> : <span className={styles.handIcon}>☝</span>}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={creating || localCreating}>
          {preview ? labels.change : labels.upload}
        </button>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(event) => chooseFile(event.target.files?.[0])} />
      </div>
      {file ? (
        <div className={styles.controls}>
          <p className={styles.selected}>{labels.selected}: {file.name}</p>
          <label><span>{labels.height}: {height.toFixed(1)} mm</span><input type="range" min="0.5" max="14" step="0.5" value={height} onChange={(event) => { clearLocalDraft(); setHeight(Number(event.target.value)); }} /></label>
          <fieldset><legend>{labels.filter}</legend>
            <label><input type="radio" checked={mode === "emboss"} onChange={() => { clearLocalDraft(); setMode("emboss"); }} />{labels.emboss}</label>
            <label><input type="radio" checked={mode === "engraved"} onChange={() => { clearLocalDraft(); setMode("engraved"); }} />{labels.engraved}</label>
            <label><input type="radio" checked={mode === "both"} onChange={() => { clearLocalDraft(); setMode("both"); }} />{labels.both}</label>
          </fieldset>
          <label className={styles.check}><input type="checkbox" checked={smooth} onChange={(event) => { clearLocalDraft(); setSmooth(event.target.checked); }} />{labels.smooth}</label>
          <button className={styles.createButton} type="button" disabled={creating || localCreating} onClick={createAiModel}>{creating ? labels.polling : labels.create}</button>
          <button className={styles.fallbackButton} type="button" disabled={creating || localCreating} onClick={createLocalModel}>{localCreating ? labels.localCreating : (localDraft ? labels.localRegenerate : labels.localCreate)}</button>
          {localDraft ? (
            <div className={styles.localDraft}>
              <p>{labels.localSize}: {localDraft.width.toFixed(1)} x {localDraft.height.toFixed(1)} x {localDraft.depth.toFixed(1)} mm</p>
              <p>{labels.localTriangles}: {localDraft.triangleCount.toLocaleString()}</p>
              <div className={styles.localDraftActions}>
                <a href={localDraft.url} download={localDraft.fileName}>{labels.localDownload}</a>
                <button type="button" onClick={uploadLocalModel} disabled={creating || localCreating}>{labels.localUpload}</button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {status ? <p className={styles.notice} aria-live="polite">{status}</p> : null}
      {authOpen ? <AuthDialog locale={locale} labels={authLabels} mode="login" returnUrl={`/diy/to3d?lang=${locale}`} onClose={() => setAuthOpen(false)} /> : null}
    </div>
  );
}
